import { Badge, Button, Col, Divider, Popconfirm, Row, Space, Switch, Tag, Tooltip, Typography, theme } from 'antd';
import { FC } from 'react';
import { BoxArrowUpRight, ExclamationCircle, Link45deg } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

import { CopyableText, DescriptionBlock, EmptyText } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';

import { DomainDeploymentInfo, DomainsSchemeRedirectTag, DomainsWWWRedirectTag } from '@/modules/Domains';
import { ProxyServerAddressesConfigTag, ProxyServerAddressesHealthTag } from '@/modules/ProxyServers';

interface Props {
  isDeployed: boolean;
  isConfigured: boolean;
  disabledByDeployStatuses: boolean;
  disabledByLoadings: boolean;
  isDetachConfigLoading: boolean;
  configInfo: NonNullable<DomainDeploymentInfo['configInfo']>;
  handleDeployDomain: () => void;
  handleDetachDeploymentConfig: () => void;
}

export const DomainConfigInfoView: FC<Props> = ({
  configInfo,
  disabledByLoadings,
  disabledByDeployStatuses,
  isDeployed,
  isDetachConfigLoading,
  handleDeployDomain,
  handleDetachDeploymentConfig
}) => {
  const { token } = theme.useToken();

  return (
    <>
      <DescriptionBlock
        layout="horizontal"
        items={ [
          [
            'Сайт',
            <a key="domain" target="_blank" href={ AppRoutes.SitesById.replace(':siteId', configInfo.site.id) } rel="noreferrer">
              { configInfo.site.name }&nbsp;
              <BoxArrowUpRight/>
            </a>
          ]
        ] }
      />

      <DescriptionBlock
        items={ [
          [
            'Проксі-сервер',
            <Row key="configInfo.proxyServer" gutter={ [16, 0] } justify="space-between" align="middle" style={{ width: '100%' }}>
              <Col span={ 24 }>
                <Link
                  style={{
                    maxWidth: 205,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block'
                  }}
                  to={ AppRoutes.ProxyServersById.replace(':proxyServerId', configInfo.proxyServer.id) }
                  target="_blank"
                >
                  { configInfo.proxyServer.name }&nbsp;
                  <BoxArrowUpRight/>
                </Link>
              </Col>
              <Col>
                <Typography.Text style={{ lineHeight: 1 }} strong>[{ configInfo.proxyServerAddress.ip }]</Typography.Text>
              </Col>
              <Col>
                <Space size="small" style={{ display: 'flex' }}>
                  <ProxyServerAddressesHealthTag simple healthStatus={ configInfo.proxyServerAddress.healthStatus }/>
                  <ProxyServerAddressesConfigTag
                    simple
                    configurationStatus={ configInfo.proxyServerAddress.configurationStatus }
                  />
                </Space>
              </Col>
            </Row>
          ]
        ] }
      />

      <DescriptionBlock
        items={ [
          [
            'DNS запис',
            <a
              href={ AppRoutes.DnsRecordsById.replace(':dnsRecordId', configInfo.dnsRecord.id) }
              target="_blank"
              key="dns"
              rel="noreferrer"
            >
              <Space>
                { configInfo.dnsRecord.name }
                <BoxArrowUpRight/>
                { configInfo.dnsRecord.apiProvider ? (
                  <Badge count={ configInfo.dnsRecord.apiProvider }/>
                ) : (
                  <Tag style={{ borderRadius: 20 }}>без провайдера</Tag>
                ) }
              </Space>
            </a>
          ]
        ] }
      />

      <DescriptionBlock
        layout="horizontal"
        items={ [['WWW перенаправлення', <DomainsWWWRedirectTag key="wwwRedirect" redirect={ configInfo.wwwRedirect }/>]] }
      />

      <DescriptionBlock
        layout="horizontal"
        items={ [
          ['HTTP/HTTPS перенаправлення', <DomainsSchemeRedirectTag key="schemeRedirect" redirect={ configInfo.schemeRedirect }/>]
        ] }
      />

      <DescriptionBlock
        layout="horizontal"
        items={ [
          [
            'Префікс статики',
            configInfo.staticPrefix
              ? <CopyableText key="configInfo.staticPrefix" textToCopy={ configInfo.staticPrefix }>
                { configInfo.staticPrefix }
              </CopyableText>
              : <EmptyText key="configInfo.staticPrefix"/>
          ]
        ] }
      />

      <Divider style={{ marginBlock: 16 }}/>

      <Row gutter={ [16, 0] } justify="space-between" align="middle" style={{ width: '100%' }}>
        <Col>
          <Space>
            <span>Розгорнути домен:</span>
            <Popconfirm
              disabled={ disabledByDeployStatuses || disabledByLoadings }
              title={ `Ви точно хочете ${ isDeployed ? 'згорнути' : 'розгорнути' } домен?` }
              onConfirm={ handleDeployDomain }
              okType="primary"
              okText="Так"
              icon={ <ExclamationCircle color={ token.colorPrimary }/> }
              placement="right"
            >
              <Switch
                disabled={ disabledByDeployStatuses || disabledByLoadings }
                loading={ disabledByDeployStatuses }
                checked={ isDeployed }
              />
            </Popconfirm>
          </Space>

        </Col>
        <Col>
          <Popconfirm
            disabled={ disabledByDeployStatuses || disabledByLoadings || isDeployed }
            title="Від’єднати домен та всі конфігурації з сайту?"
            onConfirm={ handleDetachDeploymentConfig }
            okType="danger"
            okText="Так"
            icon={ <ExclamationCircle color={ token.colorError }/> }
            placement="left"
          >
            <Tooltip placement="left" title={ isDeployed && 'Спочатку згорніть домен' }>
              <Button
                disabled={ disabledByDeployStatuses || disabledByLoadings || isDeployed }
                loading={ isDetachConfigLoading }
                type="text"
                danger
                icon={ <Link45deg size={ 16 }/> }
              >
                Відʼєднати
              </Button>
            </Tooltip>
          </Popconfirm>
        </Col>
      </Row>
    </>
  );
};
